import { useCallback, useEffect, useRef, useState } from 'react';
import { useSeller } from '@/lib/SellerContext';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { ImagePlus, Loader2, ZoomIn, Plus, Trash2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const CW = 320;

// Reusable image picker + canvas cropper (zoom + drag alignment). No external deps.
export const ImageUploader = ({ value, onChange, aspect = 1, shape = 'rect', label = 'Imagen', testid = 'image', outputWidth = 800, disabled = false, uploadFn = null }) => {
  const seller = useSeller();
  const CH = Math.round(CW / aspect);
  const fileRef = useRef(null);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef({ active: false, sx: 0, sy: 0, ox: 0, oy: 0 });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [ready, setReady] = useState(false);

  const clamp = useCallback((p, s) => {
    const iw = imgRef.current.width * s, ih = imgRef.current.height * s;
    const minX = Math.min(0, CW - iw), minY = Math.min(0, CH - ih);
    return { x: Math.max(minX, Math.min(0, p.x)), y: Math.max(minY, Math.min(0, p.y)) };
  }, [CH]);

  const draw = useCallback(() => {
    const c = canvasRef.current; if (!c || !imgRef.current) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, CW, CH);
    ctx.fillStyle = '#f1f3f5'; ctx.fillRect(0, 0, CW, CH);
    ctx.drawImage(imgRef.current, pos.x, pos.y, imgRef.current.width * scale, imgRef.current.height * scale);
  }, [CH, pos, scale]);

  useEffect(() => { if (open && ready) draw(); }, [open, ready, draw]);

  const onPick = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Selecciona un archivo de imagen'); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error('La imagen supera 8 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        imgRef.current = img;
        const base = Math.max(CW / img.width, CH / img.height);
        setMinScale(base); setScale(base);
        setPos({ x: (CW - img.width * base) / 2, y: (CH - img.height * base) / 2 });
        setReady(true); setOpen(true);
      };
      img.onerror = () => toast.error('No se pudo leer la imagen');
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
    e.target.value = '';
  };

  const onZoom = (vals) => {
    const factor = vals[0]; const s = minScale * factor;
    setScale(s); setPos((p) => clamp(p, s));
  };
  const down = (e) => { const c = canvasRef.current.getBoundingClientRect(); dragRef.current = { active: true, sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y, rx: CW / c.width, ry: CH / c.height }; };
  const move = (e) => { if (!dragRef.current.active) return; const d = dragRef.current; const nx = d.ox + (e.clientX - d.sx) * d.rx; const ny = d.oy + (e.clientY - d.sy) * d.ry; setPos(clamp({ x: nx, y: ny }, scale)); };
  const up = () => { dragRef.current.active = false; };

  const confirm = async () => {
    setBusy(true);
    try {
      const OW = outputWidth, OH = Math.round(outputWidth / aspect);
      const out = document.createElement('canvas'); out.width = OW; out.height = OH;
      const octx = out.getContext('2d'); octx.fillStyle = '#ffffff'; octx.fillRect(0, 0, OW, OH);
      const k = OW / CW;
      octx.drawImage(imgRef.current, pos.x * k, pos.y * k, imgRef.current.width * scale * k, imgRef.current.height * scale * k);
      const dataUrl = out.toDataURL('image/jpeg', 0.9);
      const base64 = dataUrl.split(',')[1];
      let url;
      if (uploadFn) { url = await uploadFn(base64, 'image/jpeg', 'upload.jpg'); }
      else { const { data } = await seller.request('post', '/seller/images', { content_base64: base64, mime_type: 'image/jpeg', filename: 'upload.jpg' }); url = data.url; }
      onChange(url && url.startsWith('http') ? url : `${BACKEND}${url}`);
      toast.success('Imagen guardada');
      setOpen(false); setReady(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar la imagen'); }
    finally { setBusy(false); }
  };

  const rounded = shape === 'circle' ? 'rounded-full' : 'rounded-md';
  return (
    <div className="space-y-2" data-testid={`uploader-${testid}`}>
      <div className="flex items-center gap-3">
        <div className={`relative h-20 w-20 border border-border ${rounded} overflow-hidden bg-muted grid place-items-center shrink-0`} style={aspect !== 1 && shape !== 'circle' ? { width: 96, height: Math.round(96 / aspect) } : {}}>
          {value ? <img src={value} alt={label} className="h-full w-full object-cover" data-testid={`uploader-preview-${testid}`} onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <ImagePlus className="text-muted-foreground" />}
          {!disabled && <button type="button" aria-label={`Subir ${label}`} data-testid={`uploader-add-${testid}`} onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-[hsl(var(--sp-accent,28_88%_52%))] text-white grid place-items-center shadow border-2 border-white"><Plus className="h-4 w-4" /></button>}
        </div>
        <div className="flex flex-col gap-1">
          {!disabled && <Button type="button" variant="outline" size="sm" data-testid={`uploader-btn-${testid}`} onClick={() => fileRef.current?.click()}><ImagePlus /> {value ? 'Cambiar imagen' : 'Subir imagen'}</Button>}
          {value && !disabled && <Button type="button" variant="ghost" size="sm" className="text-destructive" data-testid={`uploader-clear-${testid}`} onClick={() => onChange('')}><Trash2 /> Quitar</Button>}
          <span className="text-[11px] text-muted-foreground">Desde tu PC o teléfono · ajusta zoom y encuadre</span>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid={`uploader-file-${testid}`} onChange={onPick} />
      <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) { setOpen(false); setReady(false); } }}>
        <DialogContent className="seller-dialog" data-testid={`uploader-dialog-${testid}`}>
          <DialogHeader><DialogTitle>Ajustar {label}</DialogTitle><DialogDescription>Arrastra para encuadrar y usa el control de zoom. Se guardará el área visible.</DialogDescription></DialogHeader>
          <div className="flex flex-col items-center gap-4">
            <canvas ref={canvasRef} width={CW} height={CH} data-testid={`uploader-canvas-${testid}`} className={`border border-border ${shape === 'circle' ? 'rounded-full' : 'rounded-md'} touch-none cursor-move bg-muted`} style={{ maxWidth: '100%' }}
              onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} />
            <div className="flex items-center gap-3 w-full max-w-sm"><ZoomIn className="h-4 w-4 text-muted-foreground" /><Slider data-testid={`uploader-zoom-${testid}`} min={1} max={4} step={0.01} value={[scale / minScale]} onValueChange={onZoom} /></div>
          </div>
          <DialogFooter><Button type="button" variant="outline" disabled={busy} data-testid={`uploader-cancel-${testid}`} onClick={() => { setOpen(false); setReady(false); }}>Cancelar</Button><Button type="button" className="seller-primary" disabled={busy} data-testid={`uploader-save-${testid}`} onClick={confirm}>{busy && <Loader2 className="animate-spin" />} Guardar imagen</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
